import { ArrowUpRight, Search, SlidersHorizontal, TrendingDown, TrendingUp } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Pagination } from "../components/Pagination";
import { RegressionChart } from "../components/RegressionChart";
import type { PageId } from "../components/Shell";
import type { DecisionPlan, VelocityClass } from "../types";

interface ProductsPageProps {
  plan: DecisionPlan;
  onNavigate: (page: PageId) => void;
}

const categories: Array<"全部" | VelocityClass> = ["全部", "爆旺", "平销", "平滞"];
const PRODUCTS_PER_PAGE = 24;

function categoryClass(category: VelocityClass) {
  return category === "爆旺" ? "hot" : category === "平滞" ? "slow" : "stable";
}

export function ProductsPage({ plan, onNavigate }: ProductsPageProps) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<(typeof categories)[number]>("全部");
  const [page, setPage] = useState(1);
  const [selectedSku, setSelectedSku] = useState(
    plan.products.find((item) => item.sku === "10146013801")?.sku ?? plan.products[0]?.sku ?? "",
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return plan.products.filter((item) =>
      (category === "全部" || item.category === category) &&
      (!needle || item.sku.toLowerCase().includes(needle) || item.product.toLowerCase().includes(needle)),
    );
  }, [plan.products, query, category]);

  useEffect(() => setPage(1), [query, category, plan.generatedAt]);

  const paged = filtered.slice((page - 1) * PRODUCTS_PER_PAGE, page * PRODUCTS_PER_PAGE);
  const selected = paged.find((item) => item.sku === selectedSku) ?? paged[0] ?? filtered[0] ?? plan.products[0];

  return (
    <div className="products-page">
      <section className="analysis-heading">
        <div>
          <span className="section-kicker">ITEM-LEVEL DEMAND INTELLIGENCE</span>
          <h2>每条商品趋势，<br />都有自己的证据线。</h2>
        </div>
        <p>
          爆旺判断只看近 3 日与近 7 日出库量；预测采用阻尼 Holt、稳健残差与周期项，
          单日突增或骤降会被识别为异常脉冲，不会直接带偏补货量。
        </p>
      </section>

      {selected && (
        <section className="regression-workbench">
          <div className="regression-main">
            <div className="panel-head">
              <div>
                <span className="section-kicker">REGRESSION / SINGLE SKU</span>
                <h3>{selected.product}</h3>
                <small>{selected.sku} · {selected.location}</small>
              </div>
              <span className={`velocity-tag ${categoryClass(selected.category)}`}>{selected.category}</span>
            </div>
            <RegressionChart
              series={selected.series}
              forecastSeries={selected.forecastSeries}
              productName={selected.product}
              category={selected.category}
              r2={selected.r2}
              slope={selected.slope}
              slopePercent={selected.slopePercent}
            />
          </div>
          <aside className="regression-inspector">
            <div className="inspector-title">
              <span>证据读数</span>
              <SlidersHorizontal size={16} />
            </div>
            <div className="metric-stack">
              <div>
                <span>近 3 日出库</span>
                <strong>{selected.avg3d.toFixed(1)}</strong>
                <small>件 / 日</small>
              </div>
              <div>
                <span>近 7 日出库</span>
                <strong>{selected.avg7d.toFixed(1)}</strong>
                <small>件 / 日</small>
              </div>
              <div>
                <span>未来 7 日日均</span>
                <strong>{selected.forecastDaily.toFixed(1)}</strong>
                <small>90% 区间 {selected.forecastLowerDaily.toFixed(1)}–{selected.forecastUpperDaily.toFixed(1)}</small>
              </div>
              <div>
                <span>周期判定</span>
                <strong>{selected.periodDays ? `${selected.periodDays} 日` : "未检出"}</strong>
                <small>{selected.periodDays ? `信心 ${(selected.periodConfidence * 100).toFixed(0)}%` : "以阻尼趋势为主"}</small>
              </div>
            </div>
            <div className={`trend-verdict ${selected.slopePercent >= 2 ? "rising" : selected.slopePercent <= -2 ? "falling" : "level"}`}>
              {selected.slopePercent >= 2 ? <TrendingUp size={19} /> : selected.slopePercent <= -2 ? <TrendingDown size={19} /> : <span className="level-line" />}
              <div>
                <strong>
                  {selected.slopePercent >= 2 ? "需求正在抬头" : selected.slopePercent <= -2 ? "需求正在回落" : "需求大体平稳"}
                </strong>
                <p>
                  斜率 {selected.slope >= 0 ? "+" : ""}{selected.slope.toFixed(2)} 件/日，
                  R² {selected.r2.toFixed(2)}，{selected.shockDays.length
                    ? `检出 ${selected.shockDays.length} 个异常脉冲并已稳健降权。`
                    : selected.riskNote}
                  {selected.r2 < 0.32 ? " 拟合解释力偏低，仅可作为方向信号。" : ""}
                </p>
              </div>
            </div>
            <button className="primary-button full" onClick={() => onNavigate("replenishment")}>
              查看补货建议 <ArrowUpRight size={16} />
            </button>
          </aside>
        </section>
      )}

      <section className="product-directory">
        <div className="directory-toolbar">
          <div>
            <span className="section-kicker">ALL SKUS</span>
            <h3>{plan.products.length} 个品项的判断台账</h3>
          </div>
          <label className="search-box">
            <Search size={16} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索 SKU 或商品名" />
          </label>
          <div className="segmented-control" role="group" aria-label="热度筛选">
            {categories.map((item) => (
              <button key={item} className={category === item ? "is-active" : ""} onClick={() => setCategory(item)}>
                {item}
              </button>
            ))}
          </div>
        </div>

        <div className="product-table" role="table" aria-label="品项趋势表">
          <div className="product-row product-row-head" role="row">
            <span>品项</span><span>分类</span><span>近 7 日热度</span><span>近 3 日</span><span>预测日均</span><span>斜率 / 日</span><span>周期</span>
          </div>
          {paged.map((item) => (
            <button
              className={`product-row ${selected?.sku === item.sku ? "is-selected" : ""}`}
              key={item.sku}
              role="row"
              onClick={() => setSelectedSku(item.sku)}
              aria-label={`查看 ${item.product} 的回归趋势，${item.category}，斜率 ${item.slope >= 0 ? "上升" : "下降"} ${Math.abs(item.slope).toFixed(2)} 件每日`}
            >
              <span className="product-name-cell">
                <strong>{item.product}</strong>
                <small>{item.sku} · {item.location}</small>
              </span>
              <span><i className={`category-dot ${categoryClass(item.category)}`} />{item.category}</span>
              <span>{item.recentHeat.toFixed(1)}</span>
              <span>{item.avg3d.toFixed(1)}</span>
              <span>{item.forecastDaily.toFixed(1)}</span>
              <span className={item.slope >= 0 ? "positive" : "negative"}>{item.slope >= 0 ? "+" : ""}{item.slope.toFixed(2)}</span>
              <span>{item.recommendedCycle} 日</span>
            </button>
          ))}
          {!filtered.length && <div className="empty-state">没有匹配当前筛选条件的品项。</div>}
        </div>
        {filtered.length > 0 && (
          <Pagination
            page={page}
            pageSize={PRODUCTS_PER_PAGE}
            totalItems={filtered.length}
            onPageChange={setPage}
            label="品项判断台账分页"
          />
        )}
      </section>
    </div>
  );
}
